import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Download, Loader2, CalendarDays, CheckCircle2, Undo2 } from "lucide-react";
import { api, downloadFile, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProject } from "@/context/ProjectContext";
import { LOGOS, MILESTONE_BADGE, fmtDate } from "@/lib/constants";
import { PmBot } from "@/components/PmBot";

export default function MilestoneCliente() {
  const { user } = useAuth();
  const { projectId } = useProject();
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [commenti, setCommenti] = useState({});
  const [approving, setApproving] = useState("");

  const load = () =>
    api
      .get(`/projects/${projectId}/plan`)
      .then(({ data }) => setPlan(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));

  useEffect(() => {
    if (!projectId) return;
    setPlan(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const exportExcel = async () => {
    setBusy(true);
    try {
      await downloadFile(`/projects/${projectId}/export/excel`, "Milestone_aggiornate.xlsx");
      toast.success("Excel aggiornato scaricato");
    } catch (e) {
      toast.error("Errore nel download");
    }
    setBusy(false);
  };

  const approva = async (m) => {
    setApproving(m.id);
    try {
      await api.post(`/milestones/${m.id}/approva`, { commento: commenti[m.id] || "" });
      toast.success(`${m.codice} approvata`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setApproving("");
  };

  const revoca = async (m) => {
    setApproving(m.id);
    try {
      await api.post(`/milestones/${m.id}/revoca`);
      toast.success(`Approvazione di ${m.codice} revocata`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setApproving("");
  };

  if (!plan)
    return (
      <div className="h-64 grid place-items-center text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );

  const canApprove = user.role === "cliente_finale" || user.role === "admin";
  const globale =
    Math.round((plan.milestones.reduce((a, m) => a + m.percentuale, 0) / (plan.milestones.length || 1)) * 10) / 10;

  return (
    <div className="space-y-6" data-testid="milestone-cliente-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-start gap-4">
          <img src={LOGOS.cliente_finale} alt="" className="h-12 w-12 rounded-xl object-cover border border-slate-200 bg-white" />
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">{plan.project.nome}</p>
            <h1 className="mt-1 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Milestone Cliente</h1>
            <p className="mt-2 text-sm text-slate-600">
              Percentuali consolidate da Majorbit (media pesata sui giorni stimati dei task dello sprint collegato).
              {canApprove ? " Puoi approvare ogni milestone con data e commento tracciati." : ""}
            </p>
          </div>
        </div>
        <Button onClick={exportExcel} disabled={busy} variant="outline"
                className="h-10 bg-white border-slate-300 hover:bg-slate-900 hover:text-white transition-colors"
                data-testid="milestone-export-excel-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
          Scarica Excel aggiornato
        </Button>
      </div>

      <div className="bg-[#0F172A] text-white rounded-xl p-6 sm:p-8" data-testid="milestone-overview-card">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">Avanzamento complessivo</p>
            <p className="mt-2 text-5xl font-bold tracking-tight" data-testid="milestone-overall-progress">{globale}%</p>
          </div>
          <div className="text-right text-sm text-slate-300">
            <p className="flex items-center gap-2 justify-end">
              <CalendarDays className="h-4 w-4" /> {plan.project.cliente || "Cliente"}
            </p>
            <p className="mt-1">
              {plan.milestones.filter((m) => m.approvata_da).length} di {plan.milestones.length} milestone approvate
            </p>
          </div>
        </div>
        <div className="mt-6 h-2 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full bg-emerald-400 transition-[width] duration-700" style={{ width: `${globale}%` }} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {plan.milestones.map((m) => (
          <div key={m.codice}
               className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
               data-testid={`milestone-card-${m.codice}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <span className="font-mono text-xs font-bold px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200">
                  {m.codice}
                </span>
                <h3 className="mt-2 text-lg font-semibold text-slate-900 leading-snug">{m.nome}</h3>
                <p className="mt-1 text-xs text-slate-500 font-mono">Target {fmtDate(m.data)} · {m.sprint}</p>
              </div>
              <span className={`shrink-0 font-mono text-[11px] px-2.5 py-1 rounded-full border ${MILESTONE_BADGE[m.stato]}`}
                    data-testid={`milestone-status-${m.codice}`}>
                {m.stato}
              </span>
            </div>

            <div className="mt-5">
              <div className="flex justify-between items-baseline mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 font-mono">
                  % Consolidato Majorbit
                </span>
                <span className="text-2xl font-bold text-slate-900 font-mono" data-testid={`milestone-progress-${m.codice}`}>
                  {m.percentuale}%
                </span>
              </div>
              <Progress value={m.percentuale} className="h-2.5" />
              <p className="mt-2 text-xs text-slate-500">
                {m.task_completati} di {m.task_totali} task chiusi con esito OK
                {user.role !== "cliente_finale" && ` · Stefano Critelli dichiara ${m.percentuale_fornitore}%`}
              </p>
            </div>

            <div className="mt-5 pt-4 border-t border-slate-100">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 font-mono">Deliverable</p>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{m.deliverable}</p>
            </div>

            <div className="mt-5 pt-4 border-t border-slate-100">
              {m.approvata_da ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4"
                     data-testid={`milestone-approved-${m.codice}`}>
                  <p className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
                    <CheckCircle2 className="h-4 w-4" />
                    Approvata da {m.approvata_da}
                  </p>
                  <p className="mt-1 font-mono text-xs text-emerald-700">
                    {new Date(m.approvata_il).toLocaleString("it-IT")}
                  </p>
                  {m.commento_cliente && (
                    <p className="mt-2 text-sm text-emerald-900 leading-relaxed">"{m.commento_cliente}"</p>
                  )}
                  {canApprove && (
                    <Button variant="ghost" size="sm" disabled={approving === m.id} onClick={() => revoca(m)}
                            className="mt-2 h-8 text-emerald-800 hover:bg-emerald-100"
                            data-testid={`milestone-revoke-button-${m.codice}`}>
                      <Undo2 className="h-3.5 w-3.5 mr-1.5" /> Revoca approvazione
                    </Button>
                  )}
                </div>
              ) : canApprove ? (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 font-mono">
                    Approvazione cliente
                  </p>
                  <Textarea rows={2} placeholder="Commento di approvazione (opzionale)..."
                            value={commenti[m.id] || ""}
                            onChange={(e) => setCommenti({ ...commenti, [m.id]: e.target.value })}
                            className="mt-2 text-sm resize-none"
                            data-testid={`milestone-comment-input-${m.codice}`} />
                  <Button onClick={() => approva(m)} disabled={approving === m.id}
                          className="mt-2 h-9 bg-emerald-600 text-white hover:bg-emerald-700 hover:text-white transition-colors"
                          data-testid={`milestone-approve-button-${m.codice}`}>
                    {approving === m.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                    Approva milestone
                  </Button>
                </div>
              ) : (
                <p className="text-xs text-slate-400 font-mono" data-testid={`milestone-pending-${m.codice}`}>
                  In attesa di approvazione cliente
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      <PmBot pagina="milestone" />

    </div>
  );
}
